/* eslint-disable react-hooks/set-state-in-effect */
/**
 * Channels sub-tab for Product Content.
 * PIM orchestrator: Pills-on-top + ChannelEditorPanel.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe, ArrowLeftRight } from 'lucide-react'
import { Loading, Alert, AlertDescription, EmptyState } from '@/components/v2/ui'
import { useChannelDescriptions } from '@/hooks/useChannelDescriptions'
import { useChannelEditor } from '@/hooks/useChannelEditor'
import { computeInheritanceMap, countOverrides } from '@/hooks/useInheritance'
import { computeChannelCompleteness } from '@/hooks/useChannelCompleteness'
import { ChannelPillsSelector, type ChannelPillItem } from '@/components/products/v2/shared/ChannelPillsSelector'
import { ChannelEditorPanel } from './channels/ChannelEditorPanel'
import { ChannelEditorDirtyBar } from './channels/ChannelEditorDirtyBar'
import { MASTER_CHANNEL_ID, getChannelName } from './channels/channel-constants'

interface Props {
  productId: string
}

export function ProductContentChannelsTab({ productId }: Props) {
  const { t } = useTranslation()
  const { query, languages, channelIds, channelNames, pushMutation, getMaster, getChannel } =
    useChannelDescriptions(productId)

  const [selectedChannelId, setSelectedChannelId] = useState(MASTER_CHANNEL_ID)
  const [selectedLang, setSelectedLang] = useState('')
  const [translateMode, setTranslateMode] = useState(false)
  const [targetLang, setTargetLang] = useState('')

  useEffect(() => {
    if (languages.length > 0 && !selectedLang) {
      setSelectedLang(languages[0])
      setTargetLang(languages[1] ?? languages[0])
    }
  }, [languages, selectedLang])

  const masterDesc = getMaster(selectedLang)
  const channelDesc = getChannel(selectedChannelId, selectedLang)
  const isMaster = selectedChannelId === MASTER_CHANNEL_ID

  const editor = useChannelEditor({
    channelDescription: isMaster ? masterDesc : channelDesc,
    masterDescription: masterDesc,
    channelId: selectedChannelId,
    selectedLang,
    pushMutation,
  })

  const resolveChannelName = (id: string) => {
    const normalized = id.replace('--', '-')
    return channelNames[normalized] ?? channelNames[id] ?? getChannelName(id)
  }

  const pills: ChannelPillItem[] = [
    {
      id: MASTER_CHANNEL_ID,
      name: channelNames[MASTER_CHANNEL_ID] ?? getChannelName(MASTER_CHANNEL_ID),
      isMaster: true,
    },
    ...channelIds
      .filter((id) => id !== MASTER_CHANNEL_ID)
      .map((id) => {
        const desc = getChannel(id, selectedLang)
        const overrideCount = countOverrides(computeInheritanceMap(masterDesc, desc))
        const completeness = computeChannelCompleteness(desc)
        return {
          id,
          name: resolveChannelName(id),
          badgeCount: overrideCount > 0 ? overrideCount : undefined,
          badgeLabel: completeness === 100 ? '✓' : undefined,
        }
      }),
  ]

  const editorChannelName = isMaster
    ? (channelNames[MASTER_CHANNEL_ID] ?? getChannelName(MASTER_CHANNEL_ID))
    : resolveChannelName(selectedChannelId)

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading size="lg" label={t('common.loading')} />
      </div>
    )
  }

  if (query.isError) {
    return (
      <Alert variant="error">
        <AlertDescription>{t('productContent.channels.loadError')}</AlertDescription>
      </Alert>
    )
  }

  if (channelIds.length === 0) {
    return <EmptyState icon={<Globe className="h-12 w-12" />} title={t('productContent.channels.noDescriptions')} />
  }

  return (
    <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden h-full min-h-0 flex flex-col">
      {/* Row 1: Channel pills + actions (Side-by-Side toggle) */}
      <div className="flex items-center justify-between gap-3 px-3 pt-3 pb-2 bg-neutral-50 dark:bg-neutral-900/70 flex-wrap">
        <ChannelPillsSelector
          channels={pills}
          activeChannelId={selectedChannelId}
          onChange={setSelectedChannelId}
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTranslateMode((v) => !v)}
            className={`text-sm px-3 py-1 rounded border ${translateMode ? 'bg-primary-500 text-white border-primary-500' : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700'} flex items-center gap-1.5`}
            aria-pressed={translateMode}
          >
            <ArrowLeftRight className="h-3.5 w-3.5" />
            {t('productContent.channels.sideBySide')}
          </button>
        </div>
      </div>

      {/* Row 2: Language selector (own row, Q5) */}
      {languages.length > 1 && (
        <div className="flex items-center gap-2 px-3 pb-3 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/70">
          <label
            htmlFor="content-language"
            className="text-xs text-neutral-500 dark:text-neutral-400"
          >
            {t('productContent.channels.language')}
          </label>
          <select
            id="content-language"
            value={selectedLang}
            onChange={(e) => setSelectedLang(e.target.value)}
            className="text-sm px-2 py-1 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900"
            aria-label={t('productContent.channels.language')}
          >
            {languages.map((lang) => (
              <option key={lang} value={lang}>{lang.toUpperCase()}</option>
            ))}
          </select>
        </div>
      )}
      {languages.length <= 1 && (
        <div className="border-b border-neutral-200 dark:border-neutral-800" />
      )}

      <div className="flex-1 min-h-0 overflow-auto">
        <ChannelEditorPanel
          productId={productId}
          channelId={selectedChannelId}
          channelName={editorChannelName}
          description={isMaster ? masterDesc : channelDesc}
          masterDescription={masterDesc}
          isMaster={isMaster}
          translateMode={translateMode}
          translateSourceDesc={channelDesc}
          translateSourceLang={selectedLang}
          targetLang={targetLang}
          availableLanguages={languages}
          onTargetLangChange={setTargetLang}
          editFields={editor.editFields}
          inheritanceMap={editor.inheritanceMap}
          overrideCount={editor.overrideCount}
          onFieldChange={editor.updateField}
          onToggleInherit={editor.toggleOverride}
        />
      </div>
      <ChannelEditorDirtyBar
        isDirty={editor.isDirty}
        isSaving={editor.isSaving}
        changeCount={editor.overrideCount}
        onSave={editor.save}
        onDiscard={editor.discard}
      />
    </div>
  )
}
